import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { getErrorMessage, hideSpinner, showSpinner } from 'src/app/core/services/functions.service';
import { HelpersService } from 'src/app/core/services/helpers.service';
import { ReportesService } from '../services/reportes-epayco.service';

@Component({
  selector: 'app-epayco-credijamar',
  templateUrl: './epayco-credijamar.component.html',
  styleUrls: ['./epayco-credijamar.component.scss']
})
export class EpaycoCredijamarComponent implements OnInit {
  title = 'epayco-credijamar';

  /** Rango seleccionado en el date range picker */
  fechaInicio: Date | null = null;
  fechaFin: Date | null = null;

  /** No se permiten fechas futuras */
  readonly maxDate = new Date();

  /** true mientras el backend genera el archivo; deshabilita el botón */
  loading = false;

  /** Avance de la descarga por tramos: cuántos van y cuántos hay */
  progreso: { hechos: number, total: number } | null = null;

  constructor(
    private reportesService: ReportesService,
    private helpers: HelpersService,
    private activatedRoute: ActivatedRoute
  ) { }

  ngOnInit() {
    const data = this.activatedRoute.snapshot.data;
    if (data.title) {
      this.helpers.setTitle(data.title);
    }
    this.prepararSesion();
  }

  /**
   * Pide el token M2M apenas se entra a la pantalla, para que la primera
   * descarga no lo tenga que esperar. Si falla se avisa, pero la pantalla
   * sigue usable: al descargar se vuelve a intentar.
   */
  private async prepararSesion() {
    const result = await this.reportesService.prepararSesion();
    if (!result.success) {
      this.helpers.notifications.warningMessage(result.message);
    }
  }

  /** true cuando el rango está completo y es coherente */
  get rangoValido(): boolean {
    return this.validar() === null;
  }

  /**
   * Valida el rango de fechas
   * @returns el mensaje de error, o null si el rango es válido
   */
  private validar(): string | null {
    if (!this.fechaInicio || !this.fechaFin) {
      return 'Seleccione la fecha inicial y la fecha final.';
    }
    if (this.fechaFin.getTime() < this.fechaInicio.getTime()) {
      return 'La fecha final debe ser mayor o igual a la fecha inicial.';
    }
    return null;
  }

  /** Pide el reporte al backend y descarga el CSV en el navegador */
  async descargar() {
    const error = this.validar();
    if (error || !this.fechaInicio || !this.fechaFin) {
      this.helpers.notifications.warningMessage(error || 'Seleccione la fecha inicial y la fecha final.');
      return;
    }
    const fechaInicio = this.formatDate(this.fechaInicio);
    const fechaFin = this.formatDate(this.fechaFin);
    this.loading = true;
    this.progreso = null;
    showSpinner('Generando reporte...');
    try {
      const result = await this.reportesService.descargarEpaycoCredijamar(
        { fechaInicio, fechaFin },
        (hechos, total) => this.actualizarProgreso(hechos, total)
      );
      if (result.success) {
        //El nombre lleva el rango completo que pidió el usuario, no el del último tramo
        this.saveFile(result.data, `epayco-credijamar_${fechaInicio}_${fechaFin}.csv`);
        this.helpers.notifications.successMessage('Reporte descargado.');
      } else {
        this.helpers.notifications.errorMessage(result.message || 'No pudimos generar el reporte.');
      }
    } catch (error) {
      this.helpers.notifications.errorMessage(getErrorMessage(error) || 'Ocurrió un error al generar el reporte.');
    }
    this.loading = false;
    this.progreso = null;
    hideSpinner();
  }

  /**
   * Refleja el avance de la descarga por tramos en la pantalla y en el spinner
   * @param hechos tramos ya terminados
   * @param total tramos en total (puede crecer si uno se parte en dos)
   */
  private actualizarProgreso(hechos: number, total: number) {
    this.progreso = { hechos, total };
    if (total > 1) {
      showSpinner(`Generando reporte... tramo ${hechos + 1} de ${total}`);
    }
  }

  /** Limpia el rango seleccionado */
  limpiar() {
    this.fechaInicio = null;
    this.fechaFin = null;
  }

  /**
   * Dispara la descarga del archivo en el navegador
   * @param blob contenido del archivo
   * @param fileName nombre con el que se guarda
   */
  private saveFile(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Formatea la fecha como YYYY-MM-DD en hora local, que es lo que espera el backend
   * @param date fecha del datepicker
   */
  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
